import { ActionType, AssetService, IAssetModel } from "@xala/common-services";
import { Button, Form, IFormValues, NotificationService } from "@xala/common-ui";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildAssetUpdateWithAction } from "./buildAssetActionUpdatePayload";
import { validateAssetActionTypesForm } from "./assetActionTypesValidation";
import { mapAssetActionToFormValues } from "./mapAssetActionToFormValues";
import { ActionTypeScreen } from "./components/ActionTypeScreen";
import ActionTypes from "./components/ActionType";
import ActionTypeMedia from "./components/ActionTypeMedia";
import ActionTypeMediaCollection from "./components/ActionTypeMediaCollection";
import ActionTypeURL from "./components/ActionTypeURL";
import lodash from "lodash";

const assetService = new AssetService().promisify();
const notificationService = NotificationService.getInstance();

export {
  getRequiredFieldNamesForActionType,
  validateAssetActionTypesForm,
} from "./assetActionTypesValidation";

const formLayout = {
  labelCol: {
    xs: { span: 9 },
    lg: { span: 7 },
    xl: { span: 5 },
  },
  wrapperCol: {
    xs: { span: 16 },
    lg: { span: 18 },
    xl: { span: 20 },
  },
}
const buttonLayout = {
  wrapperCol: {
    style: {
      textAlign: "right",
      marginBottom: 0,
    } as React.CSSProperties,
  },
}
export interface IAssetActionTypesProps {
  asset?: IAssetModel;
  refreshAsset: () => void;
}

export const AssetActionTypes: React.FC<IAssetActionTypesProps> = ({
  asset,
  refreshAsset,
}) => {
  const [form] = Form.useForm<IFormValues>();
  const [actionType, setActionType] = useState<ActionType | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [initialFields, setInitialFields] = useState<Partial<IFormValues>>({});
  const { t } = useTranslation();

  useEffect(() => {
    if (asset?.Id == null) {
      setActionType(undefined);
      setInitialFields({});
      form.resetFields();
      return;
    }
    const { actionType: at, fields } = mapAssetActionToFormValues(asset);
    form.resetFields();
    setActionType(at);
    setInitialFields(fields);
    form.setFieldsValue(fields);
  }, [asset, form]);

  const validateForm = useCallback(
    () => validateAssetActionTypesForm(form, actionType, t),
    [form, actionType, t]
  );

  const onSubmit = async () => {
    const ok = await validateForm();
    if (!ok) {
      return;
    }
    if (asset?.Id == null) {
      notificationService.error({
        message: t(
          "ASSET_ACTION_TYPES_NO_ASSET",
          "Asset must be saved before updating action settings."
        ),
      });
      return;
    }
    if (actionType == null) {
      return;
    }

    const values = form.getFieldsValue();
    const payload = buildAssetUpdateWithAction(asset, actionType, values);

    setSaving(true);
    try {
      const result = await assetService.updateAsset(payload);
      if (result.ok) {
        notificationService.success({
          message: t(
            "ASSET_ACTION_TYPES_UPDATE_SUCCESS",
            "Action settings saved."
          ),
        });
        refreshAsset();
      } else {
        notificationService.error({
          message: t(
            "ASSET_ACTION_TYPES_UPDATE_FAILURE",
            "Could not save action settings."
          ),
          description: result.error.Message,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="CoverList">
      <Form
        form={form}
        className="AssetForm"
        {...formLayout}
        onFinish={onSubmit}
        onValuesChange={() => form.validateFields().catch(() => {})}
      >
        <ActionTypes setValue={setActionType} value={actionType} />
        <ActionTypeMedia actionType={actionType} form={form} />
        <ActionTypeScreen actionType={actionType} form={form} />
        <ActionTypeMediaCollection actionType={actionType} form={form} />
        <ActionTypeURL actionType={actionType} />
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => !lodash.isEqual(prev, cur)}
        >
          {() => {
            const currentValues = form.getFieldsValue();
            const hasChanges = !lodash.isEqual(currentValues, initialFields);

            return (
              <Form.Item {...buttonLayout}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  disabled={saving || !hasChanges}
                >
                  {t("BUTTON_SAVE", "Save")}
                </Button>
              </Form.Item>
            );
          }}
        </Form.Item>
      </Form>
    </div>
  );
};
